"use client";

import type React from "react";
import { useMemo } from "react";
import { FetchDropdown } from "@/fetch-ui/FetchDropdown";

interface PlusOneGuestsInputProps {
  /** Max guests the activity allows per signup. The selector offers 0…maxGuests. */
  maxGuests: number;
  /** Currently selected number of guests (0…maxGuests). */
  count: number;
  /** Guest names indexed by position. May be longer than `count` so typed names survive a count decrease. */
  names: string[];
  /** Called when the guest count changes. */
  onCountChange: (count: number) => void;
  /** Called when a guest name at `index` changes. */
  onNameChange: (index: number, name: string) => void;
  /** When true, blank guest names render an inline required error (after a failed submit). */
  showErrors?: boolean;
  /** Optional advisory shown above the name fields (e.g. the won't-fit → waitlist notice). */
  warning?: string | null;
}

/**
 * Attendee-facing "+1 guests" control: a single "Guests (0…max)" selector that reveals one
 * required name field per guest. Typed names are kept in the parent's array even if the count
 * is lowered, so a fat-finger doesn't lose data (the parent slices to `count` on submit).
 *
 * @param props - See PlusOneGuestsInputProps.
 * @returns The guest-count selector plus the revealed name inputs.
 */
export function PlusOneGuestsInput({
  maxGuests,
  count,
  names,
  onCountChange,
  onNameChange,
  showErrors,
  warning,
}: PlusOneGuestsInputProps): React.ReactElement {
  const countOptions = useMemo(
    () => Array.from({ length: maxGuests + 1 }, (_, i) => ({ value: String(i), label: String(i) })),
    [maxGuests]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor="signup-guest-count" className="text-sm font-medium text-gray-700">
          How many guests are you bringing?
        </label>
        <FetchDropdown
          id="signup-guest-count"
          value={String(count)}
          onChange={(e) => {
            const next = Number(e.target.value);
            onCountChange(Number.isFinite(next) ? Math.max(0, Math.min(maxGuests, Math.floor(next))) : 0);
          }}
          options={countOptions}
        />
      </div>

      {warning && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" role="status">
          {warning}
        </p>
      )}

      {Array.from({ length: count }, (_, index) => {
        const name = names[index] ?? "";
        const hasError = Boolean(showErrors) && name.trim().length === 0;
        const errorId = `signup-guest-${index}-error`;
        // Index key is intentional: guest fields are positional and only added/removed at the tail.
        return (
          <div key={index} className="flex flex-col gap-1">
            <label htmlFor={`signup-guest-${index}`} className="text-sm font-medium text-gray-700">
              Guest {index + 1}&apos;s name<span className="text-red-500"> *</span>
            </label>
            <input
              id={`signup-guest-${index}`}
              type="text"
              value={name}
              onChange={(e) => onNameChange(index, e.target.value)}
              placeholder="Their full name"
              aria-invalid={hasError || undefined}
              aria-describedby={hasError ? errorId : undefined}
              className={`rounded-lg border px-3 py-2 text-base font-medium text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:ring-1 sm:text-sm ${
                hasError
                  ? "border-red-300 focus:border-red-400 focus:ring-red-300"
                  : "border-gray-200 focus:border-indigo-300 focus:ring-indigo-300"
              }`}
            />
            {hasError && (
              <p id={errorId} role="alert" className="text-xs font-medium text-red-600">
                Guest name is required
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
